/*
 * The `@` picker: candidates grouped under headings, one highlighted, picked
 * with the mouse or by the host's ↑ ↓ ↵ handling (the host owns the text and
 * the keyboard; this is only the list). Agent chat shows people, agents and
 * files compactly; form comments show firm members with key hints.
 */
import type { FC, ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Kbd } from "@/components/ui/kbd";
import { cn } from "@/lib/utils";

import {
  candidateGroup,
  candidateKey,
  candidateLabel,
  initials,
  type MentionCandidate
} from "./mentions";

export type MentionPickerProps = {
  matches: MentionCandidate[];
  highlight: number;
  onHighlight: (index: number) => void;
  onPick: (candidate: MentionCandidate) => void;
  /** What sits before a candidate that is not a person (people get their avatar). */
  renderLeading?: (candidate: MentionCandidate) => ReactNode;
  /** The heading above a run of candidates; defaults to People, Agent(s) or Files. */
  groupLabel?: (candidate: MentionCandidate) => string;
  /** `sm` is agent chat's compact list; `md` has 32px rows and key hints. */
  size?: "sm" | "md";
  /** Positioning (the picker is absolutely placed by its host). */
  className?: string;
};

const BOX: Record<NonNullable<MentionPickerProps["size"]>, string> = {
  sm: "max-h-60 overflow-y-auto rounded-md border bg-popover p-1 shadow-md",
  md: "flex max-h-80 flex-col rounded-lg bg-elevated p-1 shadow-popover ring-1 ring-line-3"
};

export const MentionPicker: FC<MentionPickerProps> = ({
  matches,
  highlight,
  onHighlight,
  onPick,
  renderLeading,
  groupLabel = candidateGroup,
  size = "sm",
  className
}) => {
  const md = size === "md";
  const rows = matches.map((c, i) => {
    const group = groupLabel(c);
    const showHeader = i === 0 || groupLabel(matches[i - 1]) !== group;
    const active = i === highlight;
    return (
      <div key={candidateKey(c)}>
        {showHeader ? (
          <div
            role="presentation"
            className={
              md
                ? "flex h-6 items-center px-2 text-sm text-fg-3"
                : "px-2 pt-1.5 pb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            }
          >
            {group}
          </div>
        ) : null}
        <button
          type="button"
          role="option"
          aria-selected={active}
          data-kind={c.kind}
          onMouseDown={(e) => {
            e.preventDefault();
            onPick(c);
          }}
          onMouseEnter={() => onHighlight(i)}
          className={cn(
            md
              ? "flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-base text-fg"
              : "flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-xs",
            md
              ? active
                ? "bg-fill-3"
                : "hover:bg-fill-1"
              : active
              ? "bg-accent text-accent-foreground"
              : "hover:bg-muted"
          )}
        >
          {c.kind === "user" ? (
            <Avatar size={md ? "xs" : "2xs"}>
              {c.avatarUrl ? <AvatarImage src={c.avatarUrl} alt="" /> : null}
              <AvatarFallback className="text-xs">
                {initials(c.label)}
              </AvatarFallback>
            </Avatar>
          ) : (
            renderLeading?.(c) ?? null
          )}
          <span className="min-w-0 flex-1 truncate">{candidateLabel(c)}</span>
          {c.hint ? (
            <span
              className={cn(
                "shrink-0 truncate",
                md
                  ? "max-w-40 text-sm text-fg-3"
                  : "text-xs text-muted-foreground"
              )}
            >
              {c.hint}
            </span>
          ) : null}
          {md && active ? <Kbd>↵</Kbd> : null}
        </button>
      </div>
    );
  });

  return (
    <div
      role="listbox"
      aria-label="Mention"
      className={cn(BOX[size], className)}
    >
      {md ? (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto">{rows}</div>
          <div className="mt-1 flex items-center gap-3 border-t border-line-1 px-2 pt-1.5 pb-0.5 text-sm text-fg-3">
            <span className="inline-flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              Navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd>↵</Kbd>
              Mention
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd>Esc</Kbd>
              Close
            </span>
          </div>
        </>
      ) : (
        rows
      )}
    </div>
  );
};
